import React from 'react';
import { useIonAlert, useIonToast } from '@ionic/react';
import { ItemVariant, Loading, Touchable } from 'components';
import { analytics, axius, cartFx, func, images, types } from 'utils';
import { Button } from 'react-bootstrap';
import { _store, _update } from '_Store';
import { v4 as uuidv4 } from 'uuid';

interface Props {
	item: types.Item | types.ItemCart;
	cartItemId?: string;

	handleCloseModal: () => void;
}
const Item: React.FC<Props> = (props) => {
	const [presentAlert] = useIonAlert();
	const [presentToast] = useIonToast();
	const {
		__data: {
			cart,
			activeStore,
			location,
			cartDefault,
			promotions,
			is_bottom_modal,
		},
		__auth: { isLoggedIn, favorites, user },
	} = _store.useState();

	const [v, setValues] = React.useState<types.Item | any>({ variants: {} });
	const [item, setItem] = React.useState<types.Item | any>({
		preparationTime: '00:00:00',
		images: [],
		variants: [],
	});
	const [toAdd, setToAdd] = React.useState<types.Item | any>({});
	const [inCart, setInCart] = React.useState<types.Item | any>({});
	const [loading, setLoading] = React.useState(false);
	const [productQuantity, setProductQuantity] = React.useState(1);

	React.useEffect(() => {
		return () => {
			resetProps();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		setLoading(true);
		axius
			.get(`stores/${activeStore.code}/items/${props.item.code}`)
			.then((res: types.AxiusResponse) => {
				if (res.status === 200) {
					setItem(formatOptions(res.data));
					_update.data('item', formatOptions(res.data));
					analytics.trackEvent(analytics.eventTypes.VIEW_ITEM, {
						item_id: res.data.id,
						item_name: res.data.name,
						price: res.data.price,
						discount: res.data.priceDiscount,
					});
				}

				setLoading(false);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.item, is_bottom_modal.is_open]);

	React.useEffect(() => {
		if (item.id) {
			setToAdd({
				id: item.id,
				uuid: item.id,
				cartItemId: inCart.cartItemId,
				code: item.code,
				name: item.name,
				price: +(inCart.price || item.priceDiscount || item.price),
				images: item.images,
				imagesLinks: item.imagesLinks,
				priceTotal: Number(
					inCart.priceTotal || item.priceDiscount || item.price
				),
				quantity: item.quantity || 1,
				description: item.description,
				variants: inCart.variants || {},
				preparationTime: item.preparationTime,
				categories: item.categories,
			});
			setValues({ variants: inCart.variants || {} });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [item, inCart]);

	React.useEffect(() => {
		if (props.cartItemId) {
			const innCart = ((cart.items || []).find(
				(c: types.ItemCart) => c.cartItemId === props.cartItemId
			) || {}) as types.ItemCart;
			innCart?.id && setInCart(innCart);
			innCart?.id && setValues({ variants: innCart.variants });
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.cartItemId]);

	React.useEffect(() => {
		let price = toAdd.priceDiscount || toAdd.price;
		let priceTotal = price;
		for (let i = 0; i < Object.keys(v.variants).length; i++) {
			const op = Object.keys(v.variants)[i];
			for (let j = 0; j < (v.variants[op] || []).length; j++) {
				const iop = v.variants[op][j];
				if (iop.price > 0) {
					priceTotal = priceTotal + +iop.price * +iop.quantity;
				}
			}
		}
		setToAdd({
			...toAdd,
			price,
			variants: v.variants,
			priceTotal,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [v]);

	const resetProps = () => {
		setValues({ variants: {} });
		setItem({
			preparationTime: '00:00:00',
			images: [],
			variants: [],
		});
		setToAdd({});
		setInCart({});
		setLoading(false);
		setProductQuantity(1);
	};

	const add2Cart = () => {
		let qty = 0;
		const min = item.variants.reduce((a: any, b: any) => a + b.min, 0);
		for (let i = 0; i < Object.keys(v.variants).length; i++) {
			qty += v.variants[Object.keys(v.variants)[i]].reduce(
				(a: any, b: any) => a + b.quantity,
				0
			);
		}
		// return;
		// item.variants.length !== Object.keys(v['variants']).length &&
		if (min > qty) {
			presentAlert({
				header: `Options`,
				message: `You must select at least ${item.variants
					.map((po: types.Variant) => {
						return `${po.max} ${po.name}`;
					})
					.join(', ')}`,
				buttons: ['Okay'],
			});
		} else {
			if (
				!cart.store?.id ||
				activeStore.code === cart.store?.code ||
				cart.items.length === 0
			) {
				if (!props.cartItemId) {
					toAdd['cartItemId'] = uuidv4();
				}
				toAdd['quantity'] = productQuantity;
				let nCart = func.copyObject(cart) as types.Cart;
				nCart['items'] = cartFx.add(toAdd, nCart.items);
				nCart['store'] = activeStore;
				_update.data('cart', nCart);
				presentToast({
					message: `${item.name} added to cart!`,
					duration: 1500,
					position: 'top',
				});
				props.handleCloseModal();
				analytics.trackEvent(analytics.eventTypes.ADD_TO_CART, {
					id: toAdd.id,
					name: toAdd.name,
					quantity: toAdd.quantity,
					price: toAdd.priceTotal,
					discount: toAdd.discount,
				});
				// router.push(`/store/${activeStore.store}`);
			} else {
				presentAlert({
					header: `Sorry!`,
					message: `You already have item(s) at "${func.venueName(cart.store)}". You can not add this item from "${func.venueName(activeStore)}".`,
					buttons: [
						{ text: 'Okay' },
						{
							text: 'Empty cart',
							handler: () => {
								const nCart = {
									items: [] as types.Item[],
									store: {},
									...cartDefault,
								};
								nCart['items'] = cartFx.add(toAdd, nCart.items);
								nCart['store'] = activeStore;
								_update.data('cart', nCart);
								presentToast({
									message: `${item.name} added to cart!`,
									duration: 1500,
									position: 'top',
								});
								setInCart(toAdd);
							},
						},
					],
				});
			}
		}
	};

	const formatOptions = (row: types.Item) => {
		if (row.id) {
			const name = (
				(row.variants[0] || { options: [] }).options[0] || { name: '' }
			).name;
			if (!name) {
				row['variants'] = row.variants.map((opt: any) => {
					return {
						...opt,
						name: opt.name,
						options: opt.options.map((name: string) => {
							return { name, price: 0 };
						}),
					};
				});
			}

			let variants = row.variants;
			if (availableSides.length > 0) {
				variants = row.variants
					.filter(
						(opt: any) =>
							['flavour'].includes(opt.name.toLowerCase()) ||
							opt.options.filter((option: any) =>
								availableSides.includes(option.code)
							).length > 0
					)
					.map((opt: any) => {
						return {
							...opt,
							options: opt.options.filter(
								(option: any) =>
									availableSides.includes(option.code) ||
									['flavour'].includes(opt.name.toLowerCase())
							),
						};
					});
			}
			row['variants'] = variants;
			return row;
		}
		return [];
	};

	const add2Fav = () => {
		let favItems = JSON.parse(JSON.stringify(favorites)) as types.Item[];
		if (inFav.id) {
			favItems = favItems.filter((fav) => fav.code !== item.code);
			_update.auth('favorites', favItems);
			axius.delte(`users/me/favorites/${item.code}`).then((res) => {
				res.status === 200 &&
					presentToast({
						message: `Item removed from your favorites`,
						duration: 1500,
						position: 'top',
					});
			});
		} else {
			favItems = favItems.concat(item);
			_update.auth('favorites', favItems);
			axius.post(`users/me/favorites`, { itemCode: item.code }).then((res) => {
				res.status === 201 &&
					presentToast({
						message: `Item added to your favorites`,
						duration: 1500,
						position: 'top',
					});
			});
		}
	};

	const isOpen = func.isOpen(activeStore).open;
	const inFav =
		favorites.find((fav) => fav.id === item.id) || ({} as types.Item);
	const itemImage =
		(item.images[0]
			? item.imagesLinks[0]
			: activeStore?.logo
				? activeStore.logoLink
				: images.Logo) || '';
	const availableSides = activeStore.preferences?.sidesAvailable || [];
	// const hasPricedVariants = (item.variants as types.Variant[]).filter(
	// 	(iv) => iv.options.filter((ivo) => ivo.price > 0).length > 0
	// );
	const hasPromo = cartFx.myPromos({ promotions, item, user })[0];
	const discount = cartFx.promoDiscount(hasPromo, item.price);

	return (
		<div
			id="Item"
			className="py-3"
			style={{ overflow: 'scroll', maxHeight: '80vh', position: 'relative' }}
		>
			<div
				className="bg-img"
				style={{
					backgroundImage: `url(assets/img/bg.pngs)`,
					backgroundAttachment: 'fixed',
					height: '100%',
				}}
			>
				{loading ? (
					<Loading />
				) : (
					<div>
						<div className="py-3">
							<div
								// id="ItemTitle"
								// className="px-3 p-3 d-flex align-items-center position-fixed"
								style={{
									top: 0,
									left: 0,
									right: 0,
									zIndex: 999,
									background: '#fafafa',
								}}
							></div>
							<div
							// className="position-relative"
							// style={{
							// 	top: document.getElementById('ItemTitle')?.offsetHeight,
							// }}
							>
								<div className="d-flex justify-content-center px-3">
									<div
										className="bg-img animate__animated animate__fadeIn rounded"
										style={{
											height: '26vh',
											position: 'relative',
											width: '100vw',
											backgroundImage: `url("${itemImage}")`,
										}}
									>
										{hasPromo && (
											<div className="item-discount m-1">
												<i className="fa fa-gift primary fs-12" />{' '}
												{hasPromo.title}
											</div>
										)}
									</div>
								</div>

								<div className="px-3 pb-5">
									{isLoggedIn && (
										<div className="d-flex justify-content-end mt-3">
											<Touchable onClick={add2Fav}>
												<div className="d-flex align-items-center text-uppercases">
													{inFav.id ? (
														<img src={images.BookmarkFilled} alt="Bookmark" />
													) : (
														<img src={images.Bookmark} alt="Bookmark" />
													)}{' '}
													&nbsp; &nbsp;
													<span>Add to Favorites</span>
												</div>
											</Touchable>
										</div>
									)}

									{item.description && (
										<div
											className="mt-2 mb-4 py-2"
											style={{ background: '#fff' }}
										>
											<div
												dangerouslySetInnerHTML={{ __html: item.description }}
											/>
										</div>
									)}
									{item.variants.map((io: types.Variant, i: number) => (
										<ItemVariant
											key={`${io.name}_${i}`}
											id={`${io.name}_${i}`}
											v={v}
											variant={io}
											onChange={(variants) =>
												setValues((sv: any) => ({
													...sv,
													variants: { ...sv.variants, ...variants },
												}))
											}
										/>
									))}
								</div>
								<div>
									<div
										className="footers py-2 px-3 bg-white shadow-lg w-100 primary"
										style={{ position: 'fixed', bottom: 0 }}
									>
										{activeStore.preferences?.canTakeOrders &&
											isOpen &&
											((item.canDeliver &&
												(item.categories[0] || {}).canDeliver &&
												location.option === types.OrderTypes.DELIVERY) ||
												location.option === types.OrderTypes.PICKUP) && (
												<div className="d-flex justify-content-between">
													<div className="d-flex justify-content-between align-items-center fs-12 border-1 secondary-outline mr-2">
														<div>
															<div
																className="px-3 py-1"
																onClick={() => {
																	if (productQuantity > 1) {
																		setProductQuantity(productQuantity - 1);
																	}
																}}
															>
																<i className={'fa fa-minus'} />
															</div>
														</div>
														<div className="fw-500 px-2">{productQuantity}</div>
														<div>
															<div
																className="px-3 py-1"
																onClick={() => {
																	setProductQuantity(productQuantity + 1);
																}}
															>
																<i className={`fa fa-plus`} />
															</div>
														</div>
													</div>
													<Button
														onClick={() => {
															add2Cart();
														}}
														variant="secondary btn-block btn-lg text-uppercases"
														className="secondary-outline"
													>
														<div className="d-flex justify-content-center">
															<div className="mr-2">
																{inCart?.id ? 'Edit' : 'Add'}
															</div>{' '}
															<div>
																<b>
																	₦
																	{func.numberFormat(
																		inCart?.id
																			? cartFx.total(
																					[{ ...inCart, ...toAdd }],
																					true,
																					false,
																					true
																				) * productQuantity
																			: +toAdd.priceTotal * productQuantity -
																					discount * productQuantity
																	)}
																</b>
															</div>
														</div>
													</Button>
												</div>
											)}
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default Item;
